/**
 * Serviço de dados do portal de engenheiros (Fase 2).
 *
 * Espelha a lógica da app ESFAPP (RegisterPage + HomePage + ProjectDetailsPage
 * + ProfilePage + ProjectService) para o browser, usando o MESMO projeto
 * Firebase e a MESMA estrutura RTDB das apps:
 *   - User/{pushId}                          (registo do engenheiro; Id = uid)
 *   - Projects                               (projetos)
 *   - Applications/{projectId}/{userId}      (candidatura pendente = registo completo)
 *   - ApprovedApplications/{projectId}/{userId}(candidatura aceite)
 *   - Association                            (para localizar o email da associação)
 *   - emails_to_send                         (fila de emails — sem push no browser)
 *   - Storage: User/{uid}.jpg                (foto de perfil, igual à app)
 *
 * As candidaturas feitas aqui aparecem no portal das associações e na app
 * (mesma estrutura), e o registo aparece no dashboard de admin (nó "User").
 */
import {ref, set, remove} from "firebase/database";
import {getDownloadURL, ref as storageRef, uploadBytes} from "firebase/storage";
import {db, storage} from "../firebase";
import {
    fetchNode,
    postValue,
    findRecordById,
    queueEmail,
    getAllProjects,
    DEFAULT_ASSOCIATION_LOGO,
} from "./associationService";

// Foto por omissão do engenheiro (a mesma que a app usa como fallback)
export const DEFAULT_ENGINEER_PHOTO = DEFAULT_ASSOCIATION_LOGO;

// Opções fixas — as mesmas dos pickers da app ESFAPP
export const GENDERS = ["Masculino", "Feminino", "Outro"];
export const ACADEMIC_TITLES = [
    "Estudante",
    "Licenciatura",
    "Mestrado",
    "Doutoramento",
    "Licenciatura pre-Bolonha",
    "Mestrado pre-Bolonha",
];

// ---------- registo ----------

/**
 * Cria o registo do engenheiro em "User" (push, igual à app: PostAsync).
 * Grava sempre com Enabled=false — um admin ativa depois (igual à app).
 */
export async function addEngineerRecord(user) {
    return await postValue(ref(db, "User"), user);
}

/**
 * Carrega a foto para User/{uid}.jpg (mesmo caminho da app) e devolve a URL.
 * Se falhar (ex.: storage indisponível), o chamador usa DEFAULT_ENGINEER_PHOTO.
 */
export async function uploadEngineerPhoto(uid, imageFile) {
    const sref = storageRef(storage, `User/${uid}.jpg`);
    await uploadBytes(sref, imageFile);
    return await getDownloadURL(sref);
}

// ---------- perfil ----------

/** Atualiza o registo completo do engenheiro (mesma abordagem do EditProfilePage da app). */
export async function updateEngineer(key, data) {
    await set(ref(db, `User/${key}`), data);
}

/** Pontos atuais do engenheiro (lidos ao vivo do nó User). */
export async function getEngineerPoints(uid) {
    const engineer = await findEngineerById(uid);
    return (engineer && engineer.Points) || 0;
}

/** Procura o registo do engenheiro pelo Id (uid) — devolve { key, ...dados } ou null. */
export async function findEngineerById(uid) {
    return findRecordById("User", uid);
}

// ---------- projetos (listas do engenheiro) ----------

/** Projetos aprovados, mais recentes primeiro (igual à GetAllProjects da app). */
export async function getEnabledProjects() {
    const all = await getAllProjects();
    return all.filter((p) => p.Enabled).reverse();
}

/** "Para ti" — projetos do colégio do engenheiro (igual à ProjectByCollege da app). */
export async function getProjectsForCollege(course) {
    const all = await getEnabledProjects();
    return all.filter((p) => p.College === course);
}

/** "Outros" — projetos de colégios diferentes (igual à GetOtherProjects da app). */
export async function getOtherProjects(course) {
    const all = await getEnabledProjects();
    return all.filter((p) => p.College !== course);
}

/** Pesquisa em título, associação, colégio e localização (igual à ProjectSearchResult da app). */
export async function searchProjects(search) {
    const all = await getEnabledProjects();
    const s = (search || "").toLowerCase();
    if (!s) return all;
    return all.filter((p) =>
        String(p.Title || "").toLowerCase().includes(s) ||
        String(p.Association || "").toLowerCase().includes(s) ||
        String(p.College || "").toLowerCase().includes(s) ||
        String(p.Location || "").toLowerCase().includes(s)
    );
}

// ---------- candidaturas ----------

/** Associação dona de um projeto (project.Association = nome) — para o email. */
async function getAssociationForProject(project) {
    if (!project || !project.Association) return null;
    const data = await fetchNode("Association");
    if (!data) return null;
    const match = Object.values(data).find((a) => a && a.Name === project.Association);
    return match || null;
}

/**
 * Candidatar-se a um projeto — igual à app:
 * grava o registo completo do engenheiro em Applications/{projectId}/{userId}
 * e envia email à associação (a app usava push + email; no browser só email).
 */
export async function applyToProject(project, engineer) {
    await set(ref(db, `Applications/${project.Id}/${engineer.Id}`), engineer);

    const association = await getAssociationForProject(project);
    if (association && association.Email) {
        await queueEmail(
            association.Email,
            "E4IU - Nova Candidatura Recebida",
            `Olá,\n\nRecebeu uma nova candidatura do voluntário ${engineer.FirstName} ${engineer.LastName} para o projeto '${project.Title}'.\n\nAceda à plataforma para analisar e aceitar ou recusar este candidato.`
        );
    }
}

/**
 * Remover a própria candidatura pendente — igual à app:
 * apaga Applications/{projectId}/{userId} e avisa a associação por email.
 */
export async function removeMyApplication(project, engineer) {
    await remove(ref(db, `Applications/${project.Id}/${engineer.Id}`));

    const association = await getAssociationForProject(project);
    if (association && association.Email) {
        await queueEmail(
            association.Email,
            "E4IU - Candidatura Removida",
            `Olá,\n\nO voluntário ${engineer.FirstName} ${engineer.LastName} removeu a candidatura para o projeto '${project.Title}'.\n\nPode rever a lista atualizada de candidaturas na plataforma.`
        );
    }
}

/**
 * Sair de um projeto já aprovado — igual à app:
 * apaga ApprovedApplications/{projectId}/{userId} e avisa a associação por email.
 */
export async function leaveApprovedProject(project, engineer) {
    await remove(ref(db, `ApprovedApplications/${project.Id}/${engineer.Id}`));

    const association = await getAssociationForProject(project);
    if (association && association.Email) {
        await queueEmail(
            association.Email,
            "E4IU - Voluntário abandonou o projeto",
            `Olá,\n\nInformamos que o voluntário ${engineer.FirstName} ${engineer.LastName} abandonou o projeto '${project.Title}'.\n\nAceda à plataforma para gerir as vagas disponíveis.`
        );
    }
}

// ---------- estado de candidatura / listas do perfil ----------

/** Estado de um engenheiro num projeto: "applied" | "approved" | null (igual às verificações da app). */
export async function getApplicationState(projectId, engineer) {
    if (await fetchNode(`Applications/${projectId}/${engineer.Id}`)) return "applied";
    if (await fetchNode(`ApprovedApplications/${projectId}/${engineer.Id}`)) return "approved";
    return null;
}

/** Projetos com candidaturas pendentes deste engenheiro (igual à GetAppliedProjectsIds da app). */
export async function getMyAppliedProjects(engineer) {
    const data = await fetchNode("Applications");
    if (!data) return [];
    const result = [];
    for (const [projectId] of Object.entries(data)) {
        if (data[projectId] && data[projectId][engineer.Id]) {
            const proj = await findRecordById("Projects", projectId);
            if (proj && proj.Enabled) result.push(proj);
        }
    }
    return result;
}

/** Projetos em que este engenheiro foi aprovado (igual à GetAcceptedProjectsIds da app). */
export async function getMyApprovedProjects(engineer) {
    const data = await fetchNode("ApprovedApplications");
    if (!data) return [];
    const result = [];
    for (const [projectId] of Object.entries(data)) {
        if (data[projectId] && data[projectId][engineer.Id]) {
            const proj = await findRecordById("Projects", projectId);
            if (proj && proj.Enabled) result.push(proj);
        }
    }
    return result;
}

// ---------- validação do registo (mesma lógica da CheckEntryCompletion da app) ----------

/**
 * Valida a edição do perfil do engenheiro — igual aos campos editáveis da app
 * (sem foto, email, password ou termos — esses não se editam no perfil).
 */
export function validateEngineerProfileEdit(fields) {
    if (!fields.firstName.trim()) return "Indica o primeiro nome.";
    if (!fields.lastName.trim()) return "Indica o último nome.";
    if (!fields.gender) return "Escolhe o género.";
    if (fields.profileType === "Membro OE") {
        if (!fields.oeNumber.trim()) return "Indica o número OE.";
    } else {
        if (!fields.university.trim()) return "Indica a instituição de ensino.";
        if (!fields.studentNumber.trim()) return "Indica o número de estudante.";
    }
    if (!fields.academicTitle) return "Escolhe o título académico.";
    if (!fields.college) return "Escolhe o colégio da engenharia.";
    return null;
}

/**
 * Valida os campos do registo de engenheiro.
 * Devolve a mensagem do primeiro problema (PT) ou null se estiver tudo ok.
 */
export function validateEngineerRegistration(fields) {
    if (!fields.photo) return "Escolhe a tua foto de perfil.";
    if (!fields.email.trim()) return "Indica o email.";
    if (!fields.password) return "Indica a palavra-passe.";
    if (fields.password.length < 6) return "A palavra-passe deve ter pelo menos 6 caracteres.";
    if (fields.password !== fields.passwordAgain) return "As palavras-passe não coincidem.";
    if (!fields.firstName.trim()) return "Indica o primeiro nome.";
    if (!fields.lastName.trim()) return "Indica o último nome.";
    if (!fields.gender) return "Escolhe o género.";
    if (fields.profileType === "Membro OE") {
        if (!fields.oeNumber.trim()) return "Indica o número OE.";
    } else {
        if (!fields.university.trim()) return "Indica a instituição de ensino.";
        if (!fields.studentNumber.trim()) return "Indica o número de estudante.";
    }
    if (!fields.academicTitle) return "Escolhe o título académico.";
    if (!fields.college) return "Escolhe o colégio da engenharia.";
    if (!fields.terms) return "Aceita os termos de utilização para continuar.";
    return null;
}
